import {
  ModuleCatalogImportSchema,
  type AddonConfig,
  type CreateAddonInput,
  type MarketConfig,
  type ModuleCatalogImport,
} from '@etiquetachef/types';

/**
 * Lógica pura da importação do catálogo de módulos (`/billing/addons/import`,
 * F3 da #679): parse do arquivo, resolução dos países e plano NEW/EXISTS.
 *
 * Origem: #710 — 2026-09-24 13:31 +0200 (importação do catálogo, D26).
 *
 * Por quê: a tela só apresenta o que sai daqui; a escrita é sempre o
 * `createAddon` real, um por módulo. A importação **nunca sobrescreve**: SKU
 * que já existe é pulado, e as diferenças são mostradas para que alguém
 * decida editá-lo à mão (`updateAddon`, `setAddonEnabledCountries`,
 * pausar/retomar venda).
 *
 * Contrato: funções puras, sem rede.
 *
 * Usado por: `routes/ModuleCatalogImportPage.tsx` (rota `/billing/addons/import`,
 * aberta pela tela Cobrança → Add-ons; spec `docs/screens/billing-addons-import.md`),
 * admin web, que chama `createAddonCallable(row.input)` por linha `NEW`
 * (`ADMIN_PERMISSIONS.manageAddons`, ROOT/FINANCE).
 *
 * Dados: arquivo JSON digitado à mão, `ModuleCatalogImportSchema`
 * (`packages/types/src/schemas.ts`, strict — campo com nome errado reprova em
 * vez de sumir): `{ version: 1, enabled_countries?: ['*'] | 'SALEABLE' | ['BR', …],
 * modules: [{ sku, name_i18n, description_i18n, required_capabilities,
 * paused_reason? }] }` — `enabled_countries` ausente vale `["*"]` (todos os
 * países, #954). `paused_reason` presente = módulo nasce com a venda
 * pausada (D26); ausente = vendável. O add-on existente vem de
 * `/config/addons/items/{sku}` (`AddonConfigSchema`).
 *
 * Falhas conhecidas:
 *  - 2026-09-24 (#710, auditoria F3): `isAlreadyExistsError` lia a mensagem
 *    e não o `code` — SKU criado por outra aba no meio do lote aparecia como
 *    FAILED em vez de SKIPPED, e a importação não recomeçava.
 *
 * Issues: #679 (épico), #710 (importação e pausar/retomar venda).
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) rota, spec e permissão; referência ao
 *    `CouponEditorDialog` (removido) corrigida (#796).
 */

export type ImportFileResult =
  | { kind: 'OK'; file: ModuleCatalogImport }
  | { kind: 'INVALID_JSON' }
  | { kind: 'INVALID_SCHEMA'; issues: string[] };

/** JSON inválido e schema inválido são erros distintos: a tela mostra os `issues` do segundo. */
export function parseModuleCatalogFile(text: string): ImportFileResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { kind: 'INVALID_JSON' };
  }
  const parsed = ModuleCatalogImportSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      kind: 'INVALID_SCHEMA',
      issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
    };
  }
  return { kind: 'OK', file: parsed.data };
}

/**
 * `'SALEABLE'` vira os mercados com `saleable: true` **agora** (owner,
 * 2026-09-24) — o arquivo não congela uma lista de países. Ordenado para a
 * prévia e o `createAddon` receberem sempre a mesma lista.
 *
 * Origem: #710 — 2026-09-24 13:31 +0200.
 *
 * Dados: `markets` vem de `/config/markets/items/*` (lido pela página).
 *
 * Usado por: `routes/ModuleCatalogImportPage.tsx`.
 */
export function resolveImportCountries(
  spec: ModuleCatalogImport['enabled_countries'],
  markets: readonly MarketConfig[],
): string[] {
  const list = spec === 'SALEABLE' ? markets.filter((m) => m.saleable).map((m) => m.country) : spec;
  return Array.from(new Set(list)).sort();
}

/** Campo do add-on existente que diverge do arquivo. */
export type ImportDiffField = 'name' | 'description' | 'sellable' | 'countries';

export type ImportPlanRow = {
  sku: string;
  input: CreateAddonInput;
  sellable: boolean;
} & ({ status: 'NEW' } | { status: 'EXISTS'; diff: ImportDiffField[] });

/**
 * Uma linha por módulo do arquivo: `NEW` (vai ao `createAddon`) ou `EXISTS` com o diff.
 *
 * Origem: #710 — 2026-09-24 13:31 +0200.
 *
 * Contrato: puro. `EXISTS` nunca é reescrito pela importação — só mostra o
 * diff (nome, descrição, venda, países). `sellable` = sem `paused_reason`.
 *
 * Usado por: `routes/ModuleCatalogImportPage.tsx` (prévia e envio).
 */
export function planModuleCatalogImport(
  file: ModuleCatalogImport,
  existing: readonly AddonConfig[],
  countries: readonly string[],
): ImportPlanRow[] {
  const bySku = new Map(existing.map((a) => [a.sku, a]));
  return file.modules.map((m) => {
    const input: CreateAddonInput = { ...m, enabled_countries: [...countries] };
    const sellable = m.paused_reason === undefined;
    const current = bySku.get(m.sku);
    if (!current) return { sku: m.sku, input, sellable, status: 'NEW' };
    return { sku: m.sku, input, sellable, status: 'EXISTS', diff: diffAddon(current, input, sellable) };
  });
}

/** Campos em que o add-on gravado difere do arquivo (nome, descrição, venda, países). */
function diffAddon(current: AddonConfig, input: CreateAddonInput, sellable: boolean): ImportDiffField[] {
  const out: ImportDiffField[] = [];
  if (!sameI18n(current.name_i18n, input.name_i18n)) out.push('name');
  if (!sameI18n(current.description_i18n, input.description_i18n)) out.push('description');
  const currentSellable = current.disabled_at === null || current.disabled_at === undefined;
  if (currentSellable !== sellable) out.push('sellable');
  const a = [...current.enabled_countries].sort().join(',');
  const b = [...input.enabled_countries].sort().join(',');
  if (a !== b) out.push('countries');
  return out;
}

/**
 * `createAddon` recusou porque o SKU já existe (outra aba criou no meio do
 * lote). O `HttpsError` chega no cliente como `FunctionsError`: o
 * discriminador está em `code` (`functions/already-exists`), **nunca** em
 * `message`, que é só o texto do servidor ("SKU … já existe") — o mesmo
 * cuidado do `AddonEditorDialog` (o `CouponEditorDialog` citado antes não
 * existe mais).
 *
 * Origem: #710 — 2026-09-24 13:51 +0200 (auditoria F3).
 *
 * Usado por: `routes/ModuleCatalogImportPage.tsx` (linha vira SKIPPED, não FAILED).
 *
 * Falhas conhecidas: 2026-09-24 (#710) — lia a mensagem; ver docstring do módulo.
 */
export function isAlreadyExistsError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code.endsWith('already-exists');
}

function sameI18n(a: Record<string, string>, b: Record<string, string>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if (a[k] !== b[k]) return false;
  return true;
}
